import React, { useState, useEffect } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import { format } from "date-fns";
import FlightTabs from "../Flight/FlightTabs";
import Modal from "react-bootstrap/Modal";

import {
  RiFlightTakeoffLine,
  RiTimeLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiEyeLine,
  RiEditLine,
  RiCalendarLine,
  RiUserLine,
  RiBuildingLine,
  RiMailLine,
  RiAlertLine,
  RiRefreshLine,
  RiSearchLine,
  RiFilterLine,
  RiCheckDoubleLine,
  RiCloseLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiSendPlaneLine,
  RiMessage2Line,
  RiCustomerServiceLine,
} from "react-icons/ri";

const FlightAmendmentRequest = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const getTodayDate = () => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const localDate = new Date(today.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0];
  };

  // State variables matching FlightBookings
  const [amendments, setAmendments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rawResponse, setRawResponse] = useState({});

  // Pagination States matching FlightBookings
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter States matching FlightBookings
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [status, setStatus] = useState("");

  // Stats State
  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    inProgressCount: 0,
    cancelledCount: 0,
    todayRequests: 0,
    requestsByType: {}
  });

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAmendment, setSelectedAmendment] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateStatus, setUpdateStatus] = useState("");
  const [adminRemarks, setAdminRemarks] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  // Conversational/interactive chat states
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  // Fetch amendment stats with optional custom parameters
  const fetchAmendmentStats = async (customStatus = null, customFromDate = null, customToDate = null) => {
    if (!user?.cid) return;
    try {
      const useStatus = customStatus !== null ? customStatus : status;
      const useFromDate = customFromDate !== null ? customFromDate : fromDate;
      const useToDate = customToDate !== null ? customToDate : toDate;
      
      const queryParams = new URLSearchParams({
        status: useStatus || "",
        fromDate: useFromDate || "",
        toDate: useToDate || "",
        parentCompanyId: user?.cid
      }).toString();

      const apiUrl = `${config.GETAMENDMENTSTATS}?${queryParams}`;
      const response = await apiService.getSingle(apiUrl);
      const data = response?.data || response;
      setStats(data);
    } catch (error) {
      console.error("Error fetching amendment stats:", error);
    }
  };

  // Fetch amendments with optional custom parameters
  const fetchAmendments = async (pNum = 1, customStatus = null, customFromDate = null, customToDate = null) => {
    if (!user?.cid) return;
    setLoading(true);
    try {
      const useStatus = customStatus !== null ? customStatus : status;
      const useFromDate = customFromDate !== null ? customFromDate : fromDate;
      const useToDate = customToDate !== null ? customToDate : toDate;
      
      const queryParams = new URLSearchParams({
        status: useStatus || "",
        fromDate: useFromDate || "",
        toDate: useToDate || "",
        pageNumber: pNum,
        pageSize: pageSize
      }).toString();

      const apiUrl = `${config.GETFLIGHTAMENDMENTSBYPARENTCOMPANY}/${user?.cid}?${queryParams}`;
      const response = await apiService.getAll(apiUrl);

      if (response) {
        setAmendments(response.data || []);
        setPageNumber(response.pageNumber || 1);
        setPageSize(response.pageSize || 10);
        setTotalPages(response.totalPages || 0);
        setTotalRecords(response.totalRecords || 0);
        setRawResponse(response);
      }
    } catch (error) {
      console.error("Error fetching amendments:", error);
      setAmendments([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper to refresh a single expanded amendment's chat thread after replying
  const refreshSingleAmendment = async (amendmentId) => {
    try {
      const queryParams = new URLSearchParams({
        status: status || "",
        fromDate: fromDate || "",
        toDate: toDate || "",
        pageNumber: pageNumber,
        pageSize: pageSize
      }).toString();

      const apiUrl = `${config.GETFLIGHTAMENDMENTSBYPARENTCOMPANY}/${user?.cid}?${queryParams}`;
      const response = await apiService.getAll(apiUrl);
      if (response && response.data) {
        setAmendments(response.data);
        const updated = response.data.find(x => x.flightAmendmentId === amendmentId);
        if (updated) {
          setSelectedAmendment(updated);
        }
      }
    } catch (error) {
      console.error("Error refreshing amendment details:", error);
    }
  };

  // Update amendment status
  const handleUpdateStatus = async () => {
    if (!updateStatus) {
      return;
    }

    setUpdatingId(selectedAmendment?.flightAmendmentId);
    try {
      const updateData = {
        flightAmendmentId: selectedAmendment.flightAmendmentId,
        status: updateStatus,
        processedBy: user?.cid,
        adminRemarks: adminRemarks
      };

      const response = await apiService.update(config.UPDATEFLIGHTAMENDMENT, updateData);

      if (response && response.success) {
        setShowUpdateModal(false);
        setUpdateStatus("");
        setAdminRemarks("");
        setSelectedAmendment(null);
        await fetchAmendments(pageNumber);
        await fetchAmendmentStats();
      }
    } catch (error) {
      console.error("Error updating amendment:", error);
    } finally {
      setUpdatingId(null);
    }
  };

  // Send a conversation message back to the client/agent
  const handleSendReply = async () => {
    const text = replyText?.trim();
    if (!text || !selectedAmendment) return;

    setSendingReply(true);
    try {
      const payload = {
        flightAmendmentId: selectedAmendment.flightAmendmentId,
        senderId: user?.cid?.toString() || "SUPPORT",
        senderType: "SUPPORT",
        messageText: text
      };

      const response = await apiService.post(config.ADDAMENDMENTMESSAGE, payload);

      if (response && response.success) {
        setReplyText("");
        await refreshSingleAmendment(selectedAmendment.flightAmendmentId);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSendingReply(false);
    }
  };

  // View amendment details
  const handleViewAmendment = (amendment) => {
    setSelectedAmendment(amendment);
    setReplyText("");
    setShowViewModal(true);
  };

  // Open update modal
  const handleOpenUpdateModal = (amendment) => {
    setSelectedAmendment(amendment);
    setUpdateStatus(amendment.status);
    setAdminRemarks(amendment.adminRemarks || "");
    setShowUpdateModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM, yyyy hh:mm a");
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-success";
      case "rejected":
        return "bg-danger";
      case "inprogress":
        return "bg-info";
      case "cancelled":
        return "bg-secondary";
      default:
        return "bg-warning text-dark";
    }
  };

  // Pagination Logic matching FlightBookings
  const GetPageData = (number) => {
    setPageNumber(number);
    fetchAmendments(number);
  };

  const GetFirstPageData = () => {
    if (rawResponse.previousPage !== null && rawResponse.previousPage !== undefined) {
      fetchAmendments(1);
    }
  };

  const GetLastPageData = () => {
    if (rawResponse.nextPage !== null && rawResponse.nextPage !== undefined) {
      fetchAmendments(totalPages);
    }
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Filter actions - Fixed reset
  const handleSearch = () => {
    setPageNumber(1);
    fetchAmendments(1);
    fetchAmendmentStats();
  };

  const handleReset = () => {
    const today = getTodayDate();
    setFromDate(today);
    setToDate(today);
    setStatus("");
    setPageNumber(1);
    // Pass reset values directly to avoid state update delay
    fetchAmendments(1, "", today, today);
    fetchAmendmentStats("", today, today);
  };

  useEffect(() => {
    if (user && !authLoading) {
      fetchAmendments(pageNumber);
      fetchAmendmentStats();
    }
  }, [user, authLoading]);

  if (authLoading) return <div className="text-center py-5">Loading...</div>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <FlightTabs pageName="AMENDMENTREQUESTS" />
              </div>
             
              <div className="card-body">
                {/* Compact Stats Cards - Single Row - All Same Size */}
                <div className="row g-1 mb-3">
                  {/* Left side - Status Counts */}
                  <div className="col-md-6">
                    <div className="d-flex flex-wrap gap-1">
                      <div className="card bg-primary text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                        <div className="card-body py-1 px-2 text-center">
                          <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Total</small>
                          <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.totalRequests}</h6>
                        </div>
                      </div>
                      <div className="card bg-warning text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                        <div className="card-body py-1 px-2 text-center">
                          <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Pending</small>
                          <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.pendingCount}</h6>
                        </div>
                      </div>
                      <div className="card bg-success text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                        <div className="card-body py-1 px-2 text-center">
                          <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Approved</small>
                          <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.approvedCount}</h6>
                        </div>
                      </div>
                      <div className="card bg-danger text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                        <div className="card-body py-1 px-2 text-center">
                          <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Rejected</small>
                          <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.rejectedCount}</h6>
                        </div>
                      </div>
                      <div className="card bg-info text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                        <div className="card-body py-1 px-2 text-center">
                          <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>In Progress</small>
                          <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.inProgressCount}</h6>
                        </div>
                      </div>
                      <div className="card bg-secondary text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                        <div className="card-body py-1 px-2 text-center">
                          <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Cancelled</small>
                          <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.cancelledCount}</h6>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Additional Info */}
                  <div className="col-md-6">
                    <div className="d-flex flex-wrap gap-1 justify-content-end">
                      <div className="card border border-primary" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                        <div className="card-body py-1 px-2 text-center">
                          <small className="text-muted d-block" style={{ fontSize: '8px' }}>Today</small>
                          <h6 className="mb-0 text-primary" style={{ fontSize: '14px' }}>{stats.todayRequests}</h6>
                        </div>
                      </div>
                      {stats.requestsByType && Object.entries(stats.requestsByType).map(([type, count]) => (
                        <div key={type} className="card border border-secondary" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-muted d-block" style={{ fontSize: '8px' }}>{type.substring(0, 8)}</small>
                            <h6 className="mb-0" style={{ fontSize: '14px' }}>{count}</h6>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Aligned Filter Section matching FlightBookings */}
                <div className="row mb-4 g-3 bg-light p-3 rounded">
                  <div className="col-md-3">
                    <label className="form-label">From Date</label>
                    <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">To Date</label>
                    <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="">All Status</option>
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                      <option value="InProgress">In Progress</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">&nbsp;</label>
                    <div className="d-flex gap-2">
                      <button className="btn btn-primary flex-grow-1" onClick={handleSearch}>
                        <i className="ri-search-line me-1"></i> Search
                      </button>
                      <button className="btn btn-danger flex-grow-1" onClick={handleReset}>
                        <i className="ri-refresh-line me-1"></i> Reset
                      </button>
                    </div>
                  </div>
                </div>

                {/* Amendments Table */}
                <div className="table-responsive table-card p-2">
                  <table className="table align-middle border">
                    <thead className="bg-soft-success bg-gradient">
                      <tr>
                        <th>S.No</th>
                        <th>Request Date</th>
                        <th>Request Type</th>
                        <th>Request Content</th>
                        <th>Status</th>
                        <th>Requested By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : amendments.length > 0 ? (
                        amendments.map((amendment, index) => (
                          <tr key={amendment.flightAmendmentId}>
                            <td>{(pageNumber - 1) * pageSize + index + 1}</td>
                            <td>{formatDate(amendment.createdAt)}</td>
                            <td>
                              <span className="fw-semibold text-primary">{amendment.requestType}</span>
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: "250px" }}>
                                {amendment.requestContent}
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${getStatusBadge(amendment.status)}`}>
                                {amendment.status}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <RiBuildingLine className="me-1 text-muted" size={14} />
                                <small className="text-dark">{amendment.requestedBy}</small>
                              </div>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-info me-1 text-white"
                                onClick={() => handleViewAmendment(amendment)}
                                title="Open Workspace / Chat History"
                              >
                                <RiEyeLine />
                              </button>
                              <button
                                className="btn btn-sm btn-primary me-1"
                                onClick={() => handleOpenUpdateModal(amendment)}
                                title="Finalize / Update Status"
                              >
                                <RiEditLine />
                              </button>
                              <Link
                                to={`/flight/booking/${amendment.flightBookingId}`}
                                className="btn btn-sm btn-success text-white"
                                title="View Booking Details & Edit Record"
                              >
                                <RiFlightTakeoffLine />
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <RiAlertLine size={48} className="text-muted mb-2" />
                            <h5>No amendment requests found</h5>
                            <p className="text-muted">Modify filter parameters to retry.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Footer matching FlightBookings style */}
                {amendments.length > 0 && (
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing <span className="fw-semibold">{amendments.length}</span> of{" "}
                        <span className="fw-semibold">{totalRecords}</span> Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li className={`page-item ${rawResponse.previousPage === null || rawResponse.previousPage === undefined ? "disabled" : ""}`} onClick={GetFirstPageData}>
                        <Link className="page-link">First</Link>
                      </li>
                      {pageNumbers.map((number) => (
                        <li key={number} className={`page-item ${pageNumber === number ? "active" : ""}`} onClick={() => GetPageData(number)}>
                          <Link className="page-link">{number}</Link>
                        </li>
                      ))}
                      <li className={`page-item ${rawResponse.nextPage === null || rawResponse.nextPage === undefined ? "disabled" : ""}`} onClick={GetLastPageData}>
                        <Link className="page-link">Last</Link>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Amendment Details & Workspace Chat Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold">
            <RiFlightTakeoffLine className="me-2 text-primary" />
            Amendment Workspace
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAmendment && (
            <div className="container-fluid">
              <div className="row mb-3 border-bottom pb-2">
                <div className="col-md-6">
                  <label className="text-muted small d-block mb-0">Request Type</label>
                  <strong className="text-dark">{selectedAmendment.requestType}</strong>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small d-block mb-0">Current Status</label>
                  <div>
                    <span className={`badge ${getStatusBadge(selectedAmendment.status)}`}>
                      {selectedAmendment.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Requirements Summary */}
              <div className="mb-3">
                <label className="text-muted small d-block mb-1">Request Details</label>
                <div className="border rounded p-3 bg-soft-warning border-warning-subtle text-dark small">
                  {selectedAmendment.requestContent || "No requirements content provided."}
                </div>
              </div>
               <div className="row mb-2 pt-2 border-top">
                <div className="col-md-6">
                  <span className="text-muted small d-block">Requested By</span>
                  <div className="text-dark small">
                    <RiBuildingLine className="me-1 text-muted" />
                    {selectedAmendment.requestedBy}
                  </div>
                </div>
                <div className="col-md-6">
                  <span className="text-muted small d-block">Requested On</span>
                  <div className="text-dark small">
                    <RiCalendarLine className="me-1 text-muted" />
                    {formatDate(selectedAmendment.createdAt)}
                  </div>
                </div>
              </div>

              {/* Two-Way Chat Box */}
              <div className="border rounded p-3 mb-3 bg-white shadow-sm" style={{ minHeight: "200px", maxHeight: "300px", overflowY: "auto" }}>
                <span className="text-muted small d-block border-bottom pb-1 mb-3 fw-semibold">
                  <RiMessage2Line className="me-1" /> Discussion Log
                </span>

                {/* Mapping message history arrays */}
                {(selectedAmendment.messageHistory || selectedAmendment.MessageHistory || []).length > 0 ? (
                  (selectedAmendment.messageHistory || selectedAmendment.MessageHistory).map((msg, mIndex) => {
                    const isSupport = msg.senderType === "SUPPORT";
                    return (
                      <div key={msg.messageId || mIndex} className={`d-flex flex-column mb-3 ${isSupport ? "align-items-end" : "align-items-start"}`}>
                        <div
                          className={`p-2 rounded-3 text-break ${isSupport ? "bg-primary text-white border border-primary shadow-sm" : "bg-light text-dark border border-secondary-subtle"}`}
                          style={{ maxWidth: "80%" }}
                        >
                          <div className="small fw-bold mb-1" style={{ fontSize: "10px" }}>
                            {isSupport ? (
                              <span>YOU (SUPPORT)</span>
                            ) : (
                              <span className="text-muted"><RiUserLine className="me-1" />CLIENT</span>
                            )}
                          </div>
                          <div className="small">{msg.messageText}</div>
                        </div>
                        <small className="text-muted mt-1" style={{ fontSize: "9px" }}>
                          {formatDate(msg.createdAt)}
                        </small>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4 text-muted small">
                    No messaging history is recorded. Send a message below to start the discussion.
                  </div>
                )}
              </div>

              {/* Conversation Reply Input */}
              {selectedAmendment.status?.toLowerCase() !== "approved" && selectedAmendment.status?.toLowerCase() !== "rejected" && (
                <div className="input-group mb-3 shadow-sm">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type a message or response to client..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSendReply();
                      }
                    }}
                  />
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleSendReply}
                    disabled={sendingReply || !replyText.trim()}
                  >
                    {sendingReply ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <>
                        <RiSendPlaneLine className="me-1" /> Send
                      </>
                    )}
                  </button>
                </div>
              )}

             

              {selectedAmendment.adminRemarks && (
                <div className="border-top pt-2 mt-2">
                  <span className="text-muted small d-block">Closure Remarks</span>
                  <div className="p-2 bg-light rounded text-muted small">{selectedAmendment.adminRemarks}</div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          {selectedAmendment?.status === "Pending" && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowViewModal(false);
                handleOpenUpdateModal(selectedAmendment);
              }}
            >
              <RiEditLine className="me-1" />
              Finalize / Close Request
            </button>
          )}
          <button className="btn btn-danger" onClick={() => setShowViewModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Update Status Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <RiEditLine className="me-2 text-primary" />
            Finalize Amendment Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAmendment && (
            <div className="container-fluid">
              <div className="mb-3">
                <label className="form-label fw-bold">Request Details:</label>
                <div className="border rounded p-2 bg-light text-muted small">
                  <strong>[{selectedAmendment.requestType}]</strong> {selectedAmendment.requestContent}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">Update Status *</label>
                <select
                  className="form-select"
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                  required
                >
                  <option value="">Select Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved (Finalized)</option>
                  <option value="Rejected">Rejected</option>
                  <option value="InProgress">In Progress</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">Admin Remarks / Close Note</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={adminRemarks}
                  onChange={(e) => setAdminRemarks(e.target.value)}
                  placeholder="Enter final closure remarks or notes to the agent..."
                />
              </div>
              <div className="alert alert-info py-2">
                <small className="text-muted">
                  <RiUserLine className="me-1" />
                  Processing request as: <strong>{user?.companyId || user?.email}</strong>
                </small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-danger" onClick={() => setShowUpdateModal(false)}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpdateStatus}
            disabled={updatingId !== null}
          >
            {updatingId !== null ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Updating...
              </>
            ) : (
              <>
                <RiCheckDoubleLine className="me-1" />
                Save Closure
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FlightAmendmentRequest;